import React, { useState, useEffect, useMemo } from "react";
import {
    Card,
    Button,
    Typography,
    Progress,
    Input,
    Space,
    Select,
    Spin,
    message,
} from "antd";
import {
    CheckCircleOutlined,
    ReloadOutlined,
    ArrowRightOutlined,
} from "@ant-design/icons";
import { MoodQuestion, MoodOption } from "./MoodMeterDashboard";
import api from "../../../services/api";

const { Title, Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;

interface Props {
    onSubmit: (response: {
        questionId: string;
        selectedOption: MoodOption;
        note?: string;
    }) => Promise<void>;
    // userSubmittedToday: boolean;
    selectedCompany: {
        id: string | null;
        type: string | null;
        name: string;
    };
    currentUser: {
        id: string;
        name: string;
        department_id?: number;
    };
    departments?: any[];
    disabled?: boolean;
    submittedTodayIds?: string[];
    onAllQuestionsCompleted?: () => void;
}

const MoodQuestionnaire: React.FC<Props> = ({
    onSubmit,
    // userSubmittedToday,
    selectedCompany,
    currentUser,
    departments = [],
    disabled = false,
    submittedTodayIds = [],
    onAllQuestionsCompleted,
}) => {
    const signature = localStorage.getItem("signature");
    const [allQuestions, setAllQuestions] = useState<MoodQuestion[]>([]);
    const [currentQuestionIndex, setCurrentQuestionIndex] = useState<number>(0);
    const [selectedOptions, setSelectedOptions] = useState<Record<string, MoodOption>>({});
    const [notes, setNotes] = useState<Record<string, string>>({});
    const [loading, setLoading] = useState<boolean>(true);
    const [submitting, setSubmitting] = useState<boolean>(false);

    // LOCAL state for tracking submitted questions in this session
    const [locallySubmittedIds, setLocallySubmittedIds] = useState<string[]>([]);

    // Fetch today's questions when company changes
    const fetchTodayQuestions = async () => {
        if (!selectedCompany.id || !selectedCompany.type || !signature) {
            setAllQuestions([]);
            setLoading(false);
            return;
        }

        setLoading(true);
        try {
            const response = await api.post('userService/getTodayQuestions', {
                signature,
                company_id: selectedCompany.id,
                company_type: selectedCompany.type,
            });

            if (response.data?.message?.[0]?.status === true) {
                const fetchedQuestions = response.data?.message?.[0]?.data || [];
                setAllQuestions(fetchedQuestions);
            } else {
                setAllQuestions([]);
            }
        } catch (error) {
            console.error("Error fetching today's questions:", error);
            message.error("Failed to load today's questions");
            setAllQuestions([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (selectedCompany.id && selectedCompany.type) {
            fetchTodayQuestions();
            setCurrentQuestionIndex(0);
            setSelectedOptions({});
            setNotes({});
            setLocallySubmittedIds([]);
        }
    }, [selectedCompany.id, selectedCompany.type]);

    const filteredQuestions = useMemo(() => {
        const combined = locallySubmittedIds.concat(submittedTodayIds);
        const allSubmittedIds = Array.from(new Set(combined));
        if (allQuestions.length > 0 && allSubmittedIds.length > 0) {
            return allQuestions.filter(
                question => !allSubmittedIds.includes(question.id.toString())
            );
        }
        return allQuestions;
    }, [allQuestions, locallySubmittedIds, submittedTodayIds]);

    useEffect(() => {
        if (filteredQuestions.length === 0 && allQuestions.length > 0) {
            onAllQuestionsCompleted?.();
        }
    }, [filteredQuestions.length, allQuestions.length, onAllQuestionsCompleted]);

    const handleSubmitResponse = async () => {
        const currentQuestion = filteredQuestions[currentQuestionIndex];
        if (!currentQuestion) return;

        const selectedOption = selectedOptions[currentQuestion.id];
        if (!selectedOption) {
            message.error("Please select an option");
            return;
        }

        setSubmitting(true);
        try {
            await onSubmit({
                questionId: currentQuestion.id.toString(),
                selectedOption,
                note: notes[currentQuestion.id],
            });

            const submittedId = currentQuestion.id.toString();

            const newLocallySubmittedIds = [...locallySubmittedIds, submittedId];
            setLocallySubmittedIds(newLocallySubmittedIds);

            setSelectedOptions(prev => {
                const newState = { ...prev };
                delete newState[currentQuestion.id];
                return newState;
            });

            setNotes(prev => {
                const newState = { ...prev };
                delete newState[currentQuestion.id];
                return newState;
            });

            // 3. Calculate how many questions remain AFTER submission
            const remainingQuestionsCount = allQuestions.filter(
                q => !newLocallySubmittedIds.includes(q.id.toString()) &&
                    !submittedTodayIds.includes(q.id.toString())
            ).length;

            if (remainingQuestionsCount > 0) {
                setCurrentQuestionIndex(0);
            }

        } catch (error) {
            console.error("Error in submit handler:", error);
            message.error("Error submitting response");
        } finally {
            setSubmitting(false);
        }
    };

    const handleOptionSelect = (option: MoodOption) => {
        const currentQuestion = filteredQuestions[currentQuestionIndex];
        if (!currentQuestion) return;

        setSelectedOptions({
            ...selectedOptions,
            [currentQuestion.id]: option,
        });
    };

    const handleNoteChange = (note: string) => {
        const currentQuestion = filteredQuestions[currentQuestionIndex];
        if (!currentQuestion) return;

        setNotes({
            ...notes,
            [currentQuestion.id]: note,
        });
    };

    const handleNext = () => {
        if (currentQuestionIndex < filteredQuestions.length - 1) {
            setCurrentQuestionIndex(currentQuestionIndex + 1);
        }
    };

    const handlePrevious = () => {
        if (currentQuestionIndex > 0) {
            setCurrentQuestionIndex(currentQuestionIndex - 1);
        }
    };

    const handleRefreshQuestions = () => {
        fetchTodayQuestions();
    };

    const currentQuestion = filteredQuestions[currentQuestionIndex];
    const isLastQuestion = currentQuestionIndex === filteredQuestions.length - 1;
    const isFirstQuestion = currentQuestionIndex === 0;
    const isCurrentQuestionAnswered = currentQuestion ? !!selectedOptions[currentQuestion.id] : false;
    const progressPercentage = filteredQuestions.length > 0
        ? ((currentQuestionIndex + 1) / filteredQuestions.length) * 100
        : 0;

    // Show loading
    if (loading) {
        return (
            <div style={{ textAlign: 'center', padding: 50 }}>
                <Spin size="large" />
                <div style={{ marginTop: 16 }}>
                    <Text type="secondary">
                        Loading today's questions...
                    </Text>
                </div>
            </div>
        );
    }

    // Show message ONLY if no questions are available
    if (filteredQuestions.length === 0) {
        return (
            <Card
                style={{
                    textAlign: 'center',
                    padding: 32,
                    border: 'none',
                    backgroundColor: '#fafafa',
                    borderRadius: 8
                }}
            >
                <CheckCircleOutlined style={{
                    fontSize: 48,
                    color: '#52c41a',
                    marginBottom: 16
                }} />
                <Title level={4} style={{ marginBottom: 8 }}>
                    🎉 All Done for Today!
                </Title>
                <Text type="secondary" style={{ fontSize: 14, display: 'block', marginBottom: 8 }}>
                    You have answered all questions.
                </Text>
                <div style={{ marginTop: 24, display: 'flex', gap: 12, justifyContent: 'center' }}>
                    <Button
                        size="middle"
                        type="default"
                        onClick={handleRefreshQuestions}
                        icon={<ReloadOutlined />}
                    >
                        Check for New Questions
                    </Button>
                </div>
            </Card>
        );
    }

    return (
        <div style={{
            maxWidth: 400,
            margin: '0 auto',
            backgroundColor: '#ffffff',
            borderRadius: 16,
            padding: '4px',
        }}>
            {/* Header */}
            <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                marginBottom: 24
            }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                    <div style={{
                        width: 40,
                        height: 40,
                        borderRadius: '50%',
                        borderColor: '#FF4354',
                        backgroundColor: '#FF6A77',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0
                    }}>
                        <span style={{ fontSize: 20 }}>🧠</span>
                    </div>
                    <div>
                        <Title level={5} style={{ margin: 0, fontSize: 16, fontWeight: 600, color: '#232360' }}>
                            Mood Tracker
                        </Title>
                        <Text style={{ fontSize: 12, color: '#5F6388', display: 'block', marginTop: 2 }}>
                            Track your mood for better daily insights.
                        </Text>
                    </div>
                </div>

            </div>

            {/* Progress Section */}
            <div style={{ marginBottom: 32 }}>
                <Text style={{ fontSize: 13, color: '#5F6388', display: 'block', fontWeight: 600, marginBottom: 8 }}>
                    {String(currentQuestionIndex + 1).padStart(2, '0')} of {String(filteredQuestions.length).padStart(2, '0')}
                </Text>
                <Progress
                    percent={progressPercentage}
                    showInfo={false}
                    strokeColor="#1677FF"
                    trailColor="#E8E8E8"
                    strokeWidth={6}
                    style={{ marginBottom: 0 }}
                    size='default'
                />
            </div>

            {/* Question Section */}
            {currentQuestion && (
                <>
                    <div style={{ marginBottom: 32 }}>
                        <Text style={{
                            fontSize: 12,
                            color: '#768396',
                            display: 'block',
                            marginBottom: 12,
                            fontWeight: 500,
                            letterSpacing: '0.5px',
                            margin: "auto"

                        }}>
                            Question {String(currentQuestionIndex + 1).padStart(2, '0')}
                        </Text>
                        <Title level={4} style={{
                            margin: 0,
                            fontSize: 18,
                            fontWeight: 600,
                            color: '#232360',
                            lineHeight: 1.5
                        }}>
                            {currentQuestion.question_text}
                        </Title>
                    </div>

                    {/* Options */}
                    <div style={{ marginBottom: 32 }}>
                        <Space direction="vertical" size={12} style={{ width: '100%' }}>
                            {currentQuestion.options.map((option) => {
                                const isSelected = selectedOptions[currentQuestion.id]?.id === option.id;
                                return (
                                    <div
                                        key={option.id}
                                        onClick={() => handleOptionSelect(option)}
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            padding: '14px 16px',
                                            borderRadius: 22,
                                            border: isSelected ? '2px solid #52C41A' : '1px solid #E8E8E8',
                                            backgroundColor: isSelected ? '#F6FFED' : '#FFFFFF',
                                            cursor: 'pointer',
                                            transition: 'all 0.2s ease',
                                        }}
                                        onMouseEnter={(e) => {
                                            if (!isSelected) {
                                                e.currentTarget.style.borderColor = '#D9D9D9';
                                                e.currentTarget.style.backgroundColor = '#FAFAFA';
                                            }
                                        }}
                                        onMouseLeave={(e) => {
                                            if (!isSelected) {
                                                e.currentTarget.style.borderColor = '#E8E8E8';
                                                e.currentTarget.style.backgroundColor = '#FFFFFF';
                                            }
                                        }}
                                    >
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                            <span style={{ fontSize: 20 }}>{option.emoji}</span>
                                            <Text style={{
                                                fontSize: 14,
                                                fontWeight: isSelected ? 500 : 400,
                                                color: '#1a1a1a'
                                            }}>
                                                {option.text}
                                            </Text>
                                        </div>
                                        <div style={{
                                            width: 20,
                                            height: 20,
                                            borderRadius: '50%',
                                            border: isSelected ? '2px solid #52C41A' : '2px solid #D9D9D9',
                                            backgroundColor: isSelected ? '#52C41A' : 'transparent',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            transition: 'all 0.2s ease',
                                        }}>
                                            {isSelected && (
                                                <div style={{
                                                    width: 8,
                                                    height: 8,
                                                    borderRadius: '50%',
                                                    backgroundColor: '#FFFFFF'
                                                }} />
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </Space>
                    </div>

                    {/* Department Selector */}
                    {/* {!currentUser.department_id && departments.length > 0 && (
                        <div style={{ marginBottom: 16 }}>
                            <Text strong style={{ fontSize: 12, display: 'block', marginBottom: 8 }}>
                                Select your department (optional)
                            </Text>
                            <Select
                                placeholder="Select department"
                                style={{ width: '100%' }}
                                size="small"
                                onChange={(value) => {
                                    console.log("Selected department:", value);
                                }}
                            >
                                {departments.map((dept) => (
                                    <Option key={dept.key} value={dept.key}>
                                        {dept.Department} ({dept.NoOfEmployees || 0} employees)
                                    </Option>
                                ))}
                            </Select>
                        </div>
                    )} */}

                    {/* Note */}
                    {/* <div style={{ marginBottom: 24 }}>
                        <Text strong style={{ fontSize: 12, display: 'block', marginBottom: 8 }}>
                            Add a note (optional)
                        </Text>
                        <TextArea
                            rows={2}
                            placeholder="Share more details about your mood or situation..."
                            value={notes[currentQuestion.id] || ''}
                            onChange={(e) => handleNoteChange(e.target.value)}
                            maxLength={200}
                            size="small"
                            style={{ borderRadius: 6 }}
                        />
                    </div> */}

                    {/* Navigation Buttons */}
                    <div style={{ display: 'flex', gap: 12 }}>
                        {/* {!isFirstQuestion && (
                            <Button
                                size="large"
                                onClick={handlePrevious}
                                disabled={isFirstQuestion}
                                icon={<ArrowLeftOutlined />}
                                style={{
                                    flex: 1,
                                    borderRadius: 8,
                                    height: 44,
                                    fontWeight: 500,
                                    border: '1px solid #D9D9D9',
                                }}
                            >
                                Back
                            </Button>
                        )} */}
                        <Button
                            type="primary"
                            size="large"
                            onClick={handleSubmitResponse}
                            disabled={!isCurrentQuestionAnswered}
                            loading={submitting}
                            icon={!isLastQuestion && <ArrowRightOutlined />}
                            style={{
                                flex: isFirstQuestion ? 1 : 2,
                                borderRadius: 8,
                                height: 44,
                                fontWeight: 500,
                                backgroundColor: isCurrentQuestionAnswered ? '#1677FF' : '#D9D9D9',
                                borderColor: isCurrentQuestionAnswered ? '#1677FF' : '#D9D9D9',
                            }}
                        >
                            {submitting ? 'Submitting...' : isLastQuestion ? 'Submit & Finish' : 'Submit & Next'}
                        </Button>
                    </div>
                </>
            )}
        </div>
    );
};

export default MoodQuestionnaire;